import type {
  AppNotification,
  AppState,
  CheckIn,
  FeedPost,
  ItineraryItem,
  PassportStamp,
  Preferences,
  Session,
  SupportCase,
  TicketConnection,
  UserProfile,
} from '../domain/models'
import type { StoragePort } from '../domain/ports'
import { normalizeFeedChannel, normalizeUserProfile, uniqueIds } from '../domain/helpers'
import { defaultShowId } from '../fixtures/tour'
import { createInitialAppState, defaultPreferences } from './initialState'

export const APP_STATE_STORAGE_KEY = 'orbral.paradox.app.v2'
export const LEGACY_PASSPORT_STORAGE_KEY = 'orbral.paradox.caution-roster.passport.v1'

interface PersistedAccountScopeV2 {
  user: UserProfile | null
  emailNextStepPending: boolean
  selectedShowId: string
  savedShowIds: string[]
  tickets: TicketConnection[]
  itinerary: ItineraryItem[]
  fanPosts: FeedPost[]
  likedPostIds: string[]
  savedPostIds: string[]
  reportedPostIds: string[]
  sharedPostIds: string[]
  checkIns: CheckIn[]
  passportStamps: PassportStamp[]
  notifications: AppNotification[]
  preferences: Preferences
  supportCases: SupportCase[]
}

interface PersistedEnvelopeV2 {
  version: 2
  activeUserId: string | null
  session: Session | null
  anonymous: PersistedAccountScopeV2
  accounts: Record<string, PersistedAccountScopeV2>
}

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object')
const isUserProfileRecord = (value: unknown): value is UserProfile => isRecord(value)
  && typeof value.id === 'string'
  && typeof value.displayName === 'string'
  && typeof value.email === 'string'
  && typeof value.homeShowId === 'string'
  && typeof value.createdAt === 'string'
const stringArray = (value: unknown): string[] => Array.isArray(value)
  ? uniqueIds(value.filter((item): item is string => typeof item === 'string'))
  : []

const emptyScope = (): PersistedAccountScopeV2 => ({
  user: null,
  emailNextStepPending: false,
  selectedShowId: defaultShowId,
  savedShowIds: [],
  tickets: [],
  itinerary: [],
  fanPosts: [],
  likedPostIds: [],
  savedPostIds: [],
  reportedPostIds: [],
  sharedPostIds: [],
  checkIns: [],
  passportStamps: [],
  notifications: [],
  preferences: { ...defaultPreferences },
  supportCases: [],
})

const emptyEnvelope = (): PersistedEnvelopeV2 => ({
  version: 2,
  activeUserId: null,
  session: null,
  anonymous: emptyScope(),
  accounts: {},
})

const sanitizeScope = (value: unknown): PersistedAccountScopeV2 => {
  if (!isRecord(value)) return emptyScope()
  const preferences = isRecord(value.preferences)
    ? {
        ...defaultPreferences,
        ...value.preferences,
        defaultChannel: normalizeFeedChannel(
          typeof value.preferences.defaultChannel === 'string'
            ? value.preferences.defaultChannel
            : defaultPreferences.defaultChannel,
        ),
      } as Preferences
    : { ...defaultPreferences }
  return {
    user: isUserProfileRecord(value.user) ? normalizeUserProfile(value.user) : null,
    emailNextStepPending: value.emailNextStepPending === true,
    selectedShowId: typeof value.selectedShowId === 'string' ? value.selectedShowId : defaultShowId,
    savedShowIds: stringArray(value.savedShowIds),
    tickets: Array.isArray(value.tickets) ? value.tickets as TicketConnection[] : [],
    itinerary: Array.isArray(value.itinerary) ? value.itinerary as ItineraryItem[] : [],
    fanPosts: Array.isArray(value.fanPosts)
      ? (value.fanPosts as FeedPost[]).filter((post) => (
        post?.source === 'fan'
        && post.body !== 'Who is getting there early for the first set?'
        && post.body !== 'I am — aiming for doors.'
      )).map((post) => ({ ...post, channel: normalizeFeedChannel(post.channel) }))
      : [],
    likedPostIds: stringArray(value.likedPostIds),
    savedPostIds: stringArray(value.savedPostIds),
    reportedPostIds: stringArray(value.reportedPostIds),
    sharedPostIds: stringArray(value.sharedPostIds),
    checkIns: Array.isArray(value.checkIns) ? value.checkIns as CheckIn[] : [],
    passportStamps: Array.isArray(value.passportStamps) ? value.passportStamps as PassportStamp[] : [],
    notifications: Array.isArray(value.notifications) ? value.notifications as AppNotification[] : [],
    preferences,
    supportCases: Array.isArray(value.supportCases) ? value.supportCases as SupportCase[] : [],
  }
}

const sanitizeEnvelope = (value: unknown): PersistedEnvelopeV2 | null => {
  if (!isRecord(value) || value.version !== 2) return null
  const accounts: Record<string, PersistedAccountScopeV2> = {}
  if (isRecord(value.accounts)) {
    Object.entries(value.accounts).forEach(([userId, scope]) => {
      accounts[userId] = sanitizeScope(scope)
    })
  }
  return {
    version: 2,
    activeUserId: typeof value.activeUserId === 'string' ? value.activeUserId : null,
    session: isRecord(value.session) ? value.session as unknown as Session : null,
    anonymous: sanitizeScope(value.anonymous),
    accounts,
  }
}

const loadLegacyStamps = (storage: StoragePort): PassportStamp[] => {
  try {
    const raw = storage.getItem(LEGACY_PASSPORT_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || parsed.version !== 1 || !Array.isArray(parsed.stampedStopIds)) return []
    return uniqueIds(parsed.stampedStopIds.filter((id): id is string => typeof id === 'string'))
      .filter((id) => id === 'stop-01' || id === 'show-01')
      .map(() => ({
        id: 'stamp-show-01-legacy',
        showId: 'show-01',
        earnedAt: '2026-08-09T00:00:00.000Z',
        source: 'legacy' as const,
      }))
      .slice(0, 1)
  } catch {
    return []
  }
}

const readEnvelope = (storage: StoragePort): PersistedEnvelopeV2 => {
  try {
    const raw = storage.getItem(APP_STATE_STORAGE_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      const valid = sanitizeEnvelope(parsed)
      if (valid) return valid
    }
  } catch {
    // A corrupt or blocked store falls back to a clean local state.
  }
  const envelope = emptyEnvelope()
  envelope.anonymous.passportStamps = loadLegacyStamps(storage)
  return envelope
}

const mergeNotifications = (
  baseline: readonly AppNotification[],
  persisted: readonly AppNotification[],
): AppNotification[] => {
  const persistedById = new Map(persisted.map((notification) => [notification.id, notification]))
  const merged = baseline.map((notification) => {
    const saved = persistedById.get(notification.id)
    return saved?.readAt ? { ...notification, readAt: saved.readAt } : { ...notification }
  })
  persisted
    .filter((notification) => !baseline.some((seed) => seed.id === notification.id))
    .forEach((notification) => merged.push({ ...notification }))
  return merged
}

const mergeScope = (
  base: AppState,
  scope: PersistedAccountScopeV2,
  user: UserProfile | null,
  session: Session | null,
): AppState => {
  const validShowIds = new Set(base.shows.map((show) => show.id))
  const fanPosts = scope.fanPosts.filter((post) => validShowIds.has(post.showId))
  const normalizedUser = user ? normalizeUserProfile(user) : null
  const seedPostIds = new Set(base.feedPosts.map((post) => post.id))
  return {
    ...base,
    user: normalizedUser,
    session,
    emailNextStepPending: scope.emailNextStepPending,
    selectedShowId: validShowIds.has(scope.selectedShowId) ? scope.selectedShowId : base.selectedShowId,
    savedShowIds: uniqueIds(scope.savedShowIds).filter((showId) => validShowIds.has(showId)),
    tickets: scope.tickets.filter((ticket) => validShowIds.has(ticket.showId)),
    itinerary: scope.itinerary.filter((item) => validShowIds.has(item.showId)),
    feedPosts: [
      ...base.feedPosts,
      ...fanPosts.filter((post) => !seedPostIds.has(post.id)),
    ],
    likedPostIds: uniqueIds(scope.likedPostIds),
    savedPostIds: uniqueIds(scope.savedPostIds),
    reportedPostIds: uniqueIds(scope.reportedPostIds),
    sharedPostIds: uniqueIds(scope.sharedPostIds),
    checkIns: scope.checkIns.filter((checkIn) => validShowIds.has(checkIn.showId)),
    passportStamps: scope.passportStamps.filter((stamp) => validShowIds.has(stamp.showId)),
    notifications: mergeNotifications(base.notifications, scope.notifications),
    preferences: { ...defaultPreferences, ...scope.preferences },
    supportCases: scope.supportCases.map((supportCase) => ({ ...supportCase })),
  }
}

const stateToScope = (state: AppState): PersistedAccountScopeV2 => ({
  user: state.user ? { ...state.user } : null,
  emailNextStepPending: state.emailNextStepPending,
  selectedShowId: state.selectedShowId,
  savedShowIds: uniqueIds(state.savedShowIds),
  tickets: state.tickets.map((ticket) => ({ ...ticket })),
  itinerary: state.itinerary.map((item) => ({ ...item })),
  fanPosts: state.feedPosts.filter((post) => post.source === 'fan').map((post) => ({ ...post })),
  likedPostIds: uniqueIds(state.likedPostIds),
  savedPostIds: uniqueIds(state.savedPostIds),
  reportedPostIds: uniqueIds(state.reportedPostIds),
  sharedPostIds: uniqueIds(state.sharedPostIds),
  checkIns: state.checkIns.map((checkIn) => ({ ...checkIn })),
  passportStamps: state.passportStamps.map((stamp) => ({ ...stamp })),
  notifications: state.notifications.map((notification) => ({ ...notification })),
  preferences: { ...state.preferences },
  supportCases: state.supportCases.map((supportCase) => ({ ...supportCase })),
})

const writeEnvelope = (storage: StoragePort, envelope: PersistedEnvelopeV2): boolean => {
  try {
    storage.setItem(APP_STATE_STORAGE_KEY, JSON.stringify(envelope))
    return true
  } catch {
    return false
  }
}

export const loadPersistedAppState = (
  storage: StoragePort,
  online = true,
  now = new Date(),
): AppState => {
  const base = createInitialAppState(online)
  const envelope = readEnvelope(storage)
  const session = envelope.session
  const sessionIsActive = Boolean(
    session
    && session.remember
    && envelope.activeUserId === session.userId
    && Date.parse(session.expiresAt) > now.getTime(),
  )
  if (sessionIsActive && session) {
    const scope = envelope.accounts[session.userId]
    if (scope?.user?.id === session.userId) return mergeScope(base, scope, scope.user, session)
  }
  return mergeScope(base, envelope.anonymous, null, null)
}

export const loadAccountState = (
  storage: StoragePort,
  user: UserProfile,
  session: Session,
  online: boolean,
  emailNextStepPending = false,
): AppState => {
  const base = createInitialAppState(online)
  const envelope = readEnvelope(storage)
  const scope = envelope.accounts[user.id] ?? {
    ...emptyScope(),
    user,
    emailNextStepPending,
    passportStamps: envelope.anonymous.passportStamps
      .filter((stamp) => stamp.source === 'legacy')
      .map((stamp) => ({ ...stamp })),
  }
  return mergeScope(base, { ...scope, user, emailNextStepPending }, user, session)
}

export const savePersistedAppState = (storage: StoragePort, state: AppState): boolean => {
  const envelope = readEnvelope(storage)
  const scope = stateToScope(state)
  if (state.user) envelope.accounts[state.user.id] = scope
  else envelope.anonymous = scope
  envelope.activeUserId = state.session?.remember ? state.user?.id ?? null : null
  envelope.session = state.session?.remember ? { ...state.session } : null
  return writeEnvelope(storage, envelope)
}

export const clearPersistedSession = (storage: StoragePort): void => {
  const envelope = readEnvelope(storage)
  envelope.activeUserId = null
  envelope.session = null
  writeEnvelope(storage, envelope)
}

export const deletePersistedAccount = (storage: StoragePort, userId: string): void => {
  const envelope = readEnvelope(storage)
  delete envelope.accounts[userId]
  if (envelope.activeUserId === userId) {
    envelope.activeUserId = null
    envelope.session = null
  }
  writeEnvelope(storage, envelope)
}

export const resetPersistedAccountData = (storage: StoragePort, userId: string | null): void => {
  const envelope = readEnvelope(storage)
  if (userId) {
    const user = envelope.accounts[userId]?.user ?? null
    envelope.accounts[userId] = { ...emptyScope(), user }
  } else {
    envelope.anonymous = emptyScope()
  }
  writeEnvelope(storage, envelope)
}

export const readPersistedEnvelopeForTests = (storage: StoragePort): unknown => readEnvelope(storage)
